import React, { Fragment, useEffect, useState } from 'react';
import { find, noop } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { tAlt } from 'Components/utils/booking/bookingUtils';
import { ICONS } from 'Components/utils/icon/icons';
import { getFlexRatesOptions } from 'Components/widgets/flex-rates/FlexRates';
import { heights } from 'Components/widgets/navigation/Navigation.constants';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import {
  getAdditionalAccommodations,
  getFilteredAdditionalAccommodationsSets,
  getIsLoadingAdditionalAccommodations,
  getAdditionalAccommodationsError,
} from 'Store/booking/booking.selector';
import { unselectAdditionalAccommodationsFilterCategory } from 'Store/booking/additional-accommodations/additionalAccommodations.action';
import { updateFilters } from 'Store/booking/additional-accommodations/filters/filterUtils';
import { addAccommodation } from 'Store/booking/proposals/proposals.action';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { BE_TRACKING, TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import {
  TRACKING_ACCOMMODATION_FILTER_TYPES,
  TRACKING_ROOM_TYPE_BY_ACCOMMODATION_CATEGORY,
} from 'Tracking/utils';
import { getAllProposalsIdWithAlternative } from 'Views/booking/BookingEngine.utils';
import { classnames } from 'Utils/classnames/classnames.utils';
import { AsyncLoader } from 'Components/widgets/loading/async-loader/AsyncLoader';
import { FiltersBar } from 'Components/widgets/filters-bar/FiltersBar';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { ACCOMMODATION_COMFORT_ORDER } from 'Utils/accommodation/accommodation.constants';
import type { BeTrackingStorage } from 'Src/types/reservation';
import {
  filterAccommodations,
  filterReducedMobilityAccomodation,
} from './AdditionalAccommodationsUtils';
import { trackSelectRoom } from './AdditionalAccommodations.tracking';
import { AdditionalAccommodationsListWrapper } from './list/AdditionalAccommodationsListWrapper';
type Props = {
  filters: any;
  filterValues: any;
  loaderClassName?: string;
  onClose: () => void;
  onSubmit: (arg0: string | AccommodationArrangement) => void;
  processChangeAccommodation: boolean;
  product: Product;
  proposal: Proposal;
  selectedRate: string;
  setSelectedRate: (arg0: string) => void;
  streetViews?: Array<StreetViewWithAccommodationCategoryId>;
  switchOffers: (offers: Array<string>) => void;
};
export const AdditionalAccommodations = ({
  filters,
  filterValues,
  loaderClassName,
  onClose,
  onSubmit = noop,
  processChangeAccommodation = true,
  product,
  proposal,
  selectedRate,
  setSelectedRate,
  streetViews,
  switchOffers,
}: Props) => {
  const appContext = useAppContext();
  const { trackClickEvent } = useTrackEvent();
  const dispatch = useDispatch();
  const isLoading = useSelector(getIsLoadingAdditionalAccommodations) || false;
  const accommodationsSets = useSelector(getAdditionalAccommodations) || [];
  const filteredAccommodations = useSelector(getFilteredAdditionalAccommodationsSets);
  const error = useSelector(getAdditionalAccommodationsError);
  const isDataFetched = useSelector(
    (state) => state.booking.additionalAccommodations.isDataFetched,
  );
  const [visibility, setVisibility] = useState(false);
  const options = getFlexRatesOptions(proposal, appContext, 'medium');
  const selectedOption = options.find((option) => option.id === selectedRate);
  const withoutProposalAccommodationSets = filterAccommodations(proposal, accommodationsSets) || [];
  const filteredAccommodationSets = filterReducedMobilityAccomodation(
    visibility,
    filteredAccommodations,
  );
  const accomodationsIdWithLowerCategories = ACCOMMODATION_COMFORT_ORDER.slice(
    0,
    ACCOMMODATION_COMFORT_ORDER.findIndex((id) => id === proposal.accommodations?.[0]?.categoryId),
  );
  const filteredAccommodationsSetsWithHigherCategories = filteredAccommodationSets.filter(
    (accomodationSets) =>
      accomodationSets.totalDifferentialPrice.value >= 0 &&
      !accomodationsIdWithLowerCategories.includes(
        accomodationSets.accommodations?.[0]?.categoryId,
      ),
  );

  /* We need to clean roomFilters from storage since we changed the proposal, and update the roomOptions */
  const updateUpsellRevenue = (accommodationSet: AccommodationsSet, proposalQuantity: number) => {
    const storage = (trackingStorageApi.get(BE_TRACKING) || {}) as BeTrackingStorage;
    const selectedProposalId = storage.contextNumber || '';
    const totalDifferentialPrice = accommodationSet.totalDifferentialPrice;
    const roomFilters = (storage.roomFilters || []).filter(
      (roomFilter) => roomFilter.proposalId === selectedProposalId,
    );
    const roomFiltersTotalUpsellRevenue = roomFilters.reduce(
      (acc, { upsellRevenue = 0 }) => acc + upsellRevenue,
      0,
    );
    const roomOptions = storage.roomOptions || {};
    const shouldClearOptions = proposal.id !== roomOptions.proposalId;
    const newUpsellRevenue =
      roomFiltersTotalUpsellRevenue +
      (totalDifferentialPrice?.value ?? 0) +
      (shouldClearOptions ? 0 : roomOptions.upsellRevenue || 0);

    /*
      We need this information on BookingConfirmationView.
      To handle external payment provider redirection we have to store it outside of redux
     */
    trackingStorageApi.set({
      ...(storage as Record<string, unknown>),
      roomFilters: [],
      roomOptions: {
        upsell: newUpsellRevenue > 0 ? 'yes' : 'no',
        upsellCategory: 'room',
        upsellRevenue: newUpsellRevenue,
        upsellQuantity: proposalQuantity,
        upsellName: accommodationSet.accommodations[0].id,
        proposalId: proposal.id,
      },
    });
  };

  const handleAddAccommodation = (accommodationId: string | null | undefined) => {
    if (!accommodationId) {
      if (selectedRate !== proposal.price.selectedRate) {
        switchOffers(selectedOption.offers);
      }

      return onClose();
    }

    const accommodationSet: AccommodationsSet = find(withoutProposalAccommodationSets, {
      id: accommodationId,
    });
    const arrangement = accommodationSet.accommodations
      .map(({ attendeesDistribution }) => attendeesDistribution)
      .filter(Boolean);
    updateUpsellRevenue(accommodationSet, withoutProposalAccommodationSets.length);

    if (!processChangeAccommodation) {
      return onSubmit(arrangement);
    }

    trackSelectRoom(accommodationSet, appContext);
    onSubmit(proposal.id);
    dispatch(
      addAccommodation({
        arrangement,
        globalLoaderLabel: t('booking.accommodation.moreRooms.loaderLabelUpdate'),
        isJoyview: product.isJoyview,
        isUrbanOasis: product.isUrbanOasis,
        proposalsId: getAllProposalsIdWithAlternative(proposal),
        toastLabel: tAlt({
          cruiseKeyPath: 'booking.accommodation.newCabinSelection',
          product,
          resortKeyPath: 'booking.accommodation.newRoomSelection',
        }),
        appContext,
      }),
    ).then(() => {
      if (selectedRate !== proposal.price.selectedRate) {
        switchOffers(selectedOption.offers);
      }
    });
  };

  const hasFiltersEnabled = filters.some((filter) =>
    filter.options.some(({ isSelected }) => isSelected),
  );
  const handleUpdateAccomodationFilters = updateFilters(
    {
      accommodationsSets,
      filterValues,
      visibility,
    },
    dispatch,
    setVisibility,
  );
  useEffect(() => {
    setTimeout(() => {
      const target = document.querySelector('#room-proposal-title');

      if (target) {
        target.scrollIntoView();
        // the header is on top of the element that we targeted, so we have to scroll down the height of the header
        const container: HTMLElement | null | undefined = document.querySelector('.scroll-y');

        if (container) {
          container.scrollTop -= heights.NAVIGATION_LARGE_HEIGHT;
        }
      }
    }, 100);
  }, [isLoading]);
  return isLoading ? (
    <AsyncLoader
      className={classnames('mt-[25vh]', loaderClassName)}
      data-scroll-anchor="loaderAdditionalAccommodations"
      icon={ICONS.STANDALONE_MAGNIFYING_GLASS}
      iconSize="3rem"
      label={tAlt({
        cruiseKeyPath: 'booking.accommodation.moreRooms.moreCabinLoaderLabel',
        product,
        resortKeyPath: 'booking.accommodation.moreRooms.moreRoomLoaderLabel',
      })}
      theme="labeled"
    />
  ) : (
    <Fragment>
      <div className="fixed inset-x-0 top-[60px] z-2">
        <FiltersBar
          caption={t('filters.label')}
          dataTestid="AccommodationsFilters"
          filters={filters}
          hasForceClose
          isLarge
          onChange={handleUpdateAccomodationFilters}
          onClear={(name) => {
            dispatch(unselectAdditionalAccommodationsFilterCategory(name));
          }}
          onClick={(name: keyof typeof TRACKING_ACCOMMODATION_FILTER_TYPES) => {
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION_LAYER, {
              detail_click: `filter_${TRACKING_ACCOMMODATION_FILTER_TYPES[name]}`,
            });
          }}
          onClickOption={(option: FilterOptionProps) => {
            if (option.isSelected) {
              const optionValue = String(option.value ?? '');
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION_LAYER, {
                detail_click: `advanced_filter_${TRACKING_ROOM_TYPE_BY_ACCOMMODATION_CATEGORY[optionValue] ?? optionValue.toLowerCase()}`,
              });
            }
          }}
          portalName="AccommodationsFilterOptions"
        />
      </div>
      <SiteContainer className="mt-100 md:mt-80">
        <AdditionalAccommodationsListWrapper
          accommodationsSets={withoutProposalAccommodationSets}
          error={error}
          filteredAccommodationSets={
            hasFiltersEnabled
              ? filteredAccommodationSets
              : filteredAccommodationsSetsWithHigherCategories
          }
          isDataFetched={isDataFetched}
          onAddAccommodation={handleAddAccommodation}
          proposal={proposal}
          product={product}
          streetViews={streetViews}
          selectedRate={selectedRate}
          setSelectedRate={setSelectedRate}
        />
      </SiteContainer>
    </Fragment>
  );
};
